import { Suspense, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Ellipsis } from 'lucide-react';
import type { Id, ISODate } from '@contracts/common';
import { DELEGATION_NOTE_MAX, type DelegationState } from '@contracts/delegation';
import { addDays } from '@domain/dates';
import { LazyCalendar as Calendar } from '@/components/ui/date-picker';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { ApiClientError } from '../../api/client';
import { isFieldRefusal, useDelegationWrite } from './api';
import { dueDate, isoOf, localDate, QUESTION_MAX } from './format';
import { useToastClearance } from './toast-clearance';
import './delegation.css';


export type RespondControlsProps = {
  itemId: Id; round: number; version: number; deadline: ISODate; state: DelegationState;
  stateBefore: DelegationState | null;
  /** Where the two popovers open and where the focus goes back (a row's "…" menu, T12); the button by default. */
  anchor?: RefObject<HTMLElement | null>;
};

/**
 * AI S9 L3 T5 (§7.1, P15; mockup §1, §2): the assignee's buttons, shared by the card, the bell and "Nhờ tôi".
 * waiting → Nhận · Xin lùi hạn · Hỏi lại; accepted → Xin lùi hạn · Hỏi lại; question → Đã rõ (+ Nhận when it was asked
 * before accepting); a deadline request waits for the delegator. Every send carries the row's `round` and `version`.
 */
export function RespondControls({ itemId, round, version, deadline, state, stateBefore, anchor }: RespondControlsProps) {
  const write = useDelegationWrite(itemId);
  const busy = write.isPending;
  const accept = () => write.mutate({ op: 'respond', body: { action: 'accept', round, version } });
  const clarified = () => write.mutate({ op: 'respond', body: { action: 'clarified', round, version } });
  if (state === 'deadline_requested') return null;
  if (state === 'question') {
    return (
      <>
        <button type="button" className="wp-btn wp-n-pri" disabled={busy} onClick={clarified}>Đã rõ</button>
        {stateBefore === 'waiting' && <button type="button" className="wp-btn" disabled={busy} onClick={accept}>Nhận</button>}
      </>
    );
  }
  return (
    <>
      {state === 'waiting' && <button type="button" className="wp-btn wp-n-pri" disabled={busy} onClick={accept}>Nhận</button>}
      <DeadlinePopover itemId={itemId} round={round} version={version} deadline={deadline} anchor={anchor} disabled={busy} />
      <QuestionPopover itemId={itemId} round={round} version={version} anchor={anchor} disabled={busy} />
    </>
  );
}

/**
 * AI S9 L3 T12 (P12, D92; mockup §6): a waiting row's three answers on a phone, behind a "…". Xin lùi hạn and Hỏi lại
 * open their popover at the "…" (mounted only while chosen, so each opens empty), and the focus goes back to it.
 */
export function RespondMenu({ itemId, round, version, deadline, title }: {
  itemId: Id; round: number; version: number; deadline: ISODate; title: string;
}) {
  const write = useDelegationWrite(itemId);
  const trigger = useRef<HTMLButtonElement>(null);
  const [chosen, setChosen] = useState<'deadline' | 'question' | null>(null);
  // The menu gives the focus back to the "…" as it closes; the popover mounts after that and takes it (measured on
  // ios390: no preventDefault needed).
  const choose = (next: 'deadline' | 'question') => () => setChosen(next);
  const close = () => setChosen(null);
  const busy = write.isPending;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button ref={trigger} type="button" className="wp-btn wp-dg-more" aria-label={`Thao tác ${title}`} disabled={busy}>
            <Ellipsis aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" collisionPadding={12}>
          <DropdownMenuItem onSelect={() => write.mutate({ op: 'respond', body: { action: 'accept', round, version } })}>
            Nhận
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={choose('deadline')}>Xin lùi hạn</DropdownMenuItem>
          <DropdownMenuItem onSelect={choose('question')}>Hỏi lại</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {chosen === 'deadline' && (
        <DeadlinePopover itemId={itemId} round={round} version={version} deadline={deadline} anchor={trigger} disabled={busy} onClose={close} />
      )}
      {chosen === 'question' && (
        <QuestionPopover itemId={itemId} round={round} version={version} anchor={trigger} disabled={busy} onClose={close} />
      )}
    </>
  );
}

/**
 * A failed send closes its popover only when the card moved under it (409: the block is read again and the old choice
 * no longer fits). Offline, 5xx or 429 keep it open with what was typed, so a retry is one press (lens 1 M4).
 */
const closesOn = (e: unknown): boolean => e instanceof ApiClientError && e.status === 409;

type PopProps = {
  itemId: Id; round: number; version: number; anchor?: RefObject<HTMLElement | null>; disabled: boolean;
  /** Opened from a menu (`RespondMenu`): no trigger of its own, open from the start, and this unmounts it on close. */
  onClose?: () => void;
};

/** Open state for a popover: its own, or — opened from a menu — open until it closes and tells the menu. */
function useOpen(onClose: (() => void) | undefined): [boolean, (next: boolean) => void] {
  const [open, setOpen] = useState(onClose !== undefined);
  return [open, (next) => { setOpen(next); if (!next) onClose?.(); }];
}

/** A trigger and its popover; with an `anchor`, the popover sits there and the focus goes back there. */
function Pop({ word, open, setOpen, anchor, disabled, trigger = true, children }: {
  word: string; open: boolean; setOpen: (next: boolean) => void; anchor?: RefObject<HTMLElement | null>;
  disabled: boolean; trigger?: boolean; children: ReactNode;
}) {
  const padding = useToastClearance(open);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      {trigger && <PopoverTrigger type="button" className="wp-btn" disabled={disabled}>{word}</PopoverTrigger>}
      {anchor !== undefined && <PopoverAnchor virtualRef={anchor} />}
      <PopoverContent
        role="dialog" aria-label={word} align="start" collisionPadding={padding}
        className="wp-dg-pop w-[min(20rem,calc(100vw-24px))] pointer-coarse:w-[min(22rem,calc(100vw-24px))] p-3"
        onCloseAutoFocus={anchor === undefined ? undefined : (e) => { e.preventDefault(); anchor.current?.focus(); }}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

function DeadlinePopover({ itemId, round, version, deadline, anchor, disabled, onClose }: PopProps & { deadline: ISODate }) {
  const write = useDelegationWrite(itemId);
  const [open, setOpen] = useOpen(onClose);
  const first = localDate(addDays(deadline, 1));
  const [day, setDay] = useState<ISODate | null>(null);
  const [month, setMonth] = useState<Date>(first);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ids = useId();
  const toggle = (next: boolean) => {
    if (next) { setDay(null); setMonth(first); setNote(''); setError(null); }
    setOpen(next);
  };
  const send = () => {
    if (day === null) return;
    const text = note.trim();
    setError(null);
    write.mutate(
      { op: 'respond', body: { action: 'request_deadline', round, version, deadline: day, ...(text === '' ? {} : { note: text }) } },
      {
        onSuccess: () => setOpen(false),
        onError: (e) => {
          if (isFieldRefusal(e)) setError(e instanceof ApiClientError ? e.message : null);
          else if (closesOn(e)) setOpen(false);
        },
      },
    );
  };
  return (
    <Pop word="Xin lùi hạn" open={open} setOpen={toggle} anchor={anchor} disabled={disabled} trigger={onClose === undefined}>
      <div className="wp-dg-form">
        <p className="wp-dg-pop-h">Xin lùi hạn</p>
        <p className="wp-dg-cnt">{`Hạn hiện tại: ${dueDate(deadline)}`}</p>
        <Suspense fallback={<p className="wp-dg-cal-wait">Đang tải lịch…</p>}>
          <Calendar
            selected={day === null ? undefined : localDate(day)} month={month} onMonthChange={setMonth}
            today={localDate(deadline)} startMonth={first} disabled={[{ before: first }]}
            onSelect={(d) => { setDay(isoOf(d)); setError(null); }}
          />
        </Suspense>
        {error !== null && <p className="wp-field-error" role="alert">{error}</p>}
        <Label htmlFor={`${ids}-note`} className="wp-dg-label">Lý do (không bắt buộc)</Label>
        <Textarea
          id={`${ids}-note`} value={note} maxLength={DELEGATION_NOTE_MAX} rows={2}
          aria-describedby={`${ids}-count`} onChange={(e) => setNote(e.target.value)}
        />
        <p className="wp-dg-count" id={`${ids}-count`}>{`${note.length}/${DELEGATION_NOTE_MAX}`}</p>
        <div className="wp-dg-ft">
          <button type="button" className="wp-btn" onClick={() => setOpen(false)}>Hủy</button>
          <button type="button" className="wp-btn wp-n-pri" disabled={day === null || write.isPending} onClick={send}>
            Gửi yêu cầu
          </button>
        </div>
      </div>
    </Pop>
  );
}

function QuestionPopover({ itemId, round, version, anchor, disabled, onClose }: PopProps) {
  const write = useDelegationWrite(itemId);
  const [open, setOpen] = useOpen(onClose);
  const [text, setText] = useState('');
  const ids = useId();
  const toggle = (next: boolean) => {
    if (next) setText('');
    setOpen(next);
  };
  const send = () => {
    const question = text.trim();
    if (question === '') return;
    write.mutate(
      { op: 'respond', body: { action: 'question', round, version, text: question } },
      { onSuccess: () => setOpen(false), onError: (e) => { if (closesOn(e)) setOpen(false); } },
    );
  };
  return (
    <Pop word="Hỏi lại" open={open} setOpen={toggle} anchor={anchor} disabled={disabled} trigger={onClose === undefined}>
      <div className="wp-dg-form">
        <Label htmlFor={`${ids}-q`} className="wp-dg-label">Câu hỏi</Label>
        <Textarea id={`${ids}-q`} value={text} maxLength={QUESTION_MAX} rows={3} onChange={(e) => setText(e.target.value)} />
        <div className="wp-dg-ft">
          <button type="button" className="wp-btn" onClick={() => setOpen(false)}>Hủy</button>
          <button type="button" className="wp-btn wp-n-pri" disabled={text.trim() === '' || write.isPending} onClick={send}>
            Gửi
          </button>
        </div>
      </div>
    </Pop>
  );
}
